import { getMeta, setMeta } from '../data/appMeta';
import type { SqlDriver } from '../data/db/driver';
import type { Cloud } from '../sync/cloud';
import { NO_ENTITLEMENT, type Entitlement, type EntitlementSource } from './entitlement';

/** The account's last answer, for when the server cannot be reached. */
export const ACCOUNT_PREMIUM_KEY = 'premium.account';

/**
 * Premium as the account knows it (sync_premium_status): the web, which has
 * no store, and a phone whose purchase was made on another device. Signed
 * out: none. Offline: the last answer, until the date it was good for.
 */
export function accountEntitlement(cloud: Cloud | null, db: SqlDriver, now: () => number = Date.now): EntitlementSource {
  return {
    async check() {
      if (!cloud) return NO_ENTITLEMENT;
      try {
        if (!(await cloud.account())) {
          await setMeta(db, ACCOUNT_PREMIUM_KEY, '');
          return NO_ENTITLEMENT;
        }
        const p = await cloud.transport.premium();
        const e: Entitlement = p.active ? { active: true, expiresAt: p.expiresAt, source: 'account', willRenew: p.willRenew } : NO_ENTITLEMENT;
        await setMeta(db, ACCOUNT_PREMIUM_KEY, e.active ? JSON.stringify(e) : '');
        return e;
      } catch (err) {
        const cached = await getMeta(db, ACCOUNT_PREMIUM_KEY).catch(() => undefined);
        const e = cached ? (JSON.parse(cached) as Entitlement) : null;
        if (e?.active && (e.expiresAt === null || e.expiresAt > now())) return e;
        console.warn('[premium] account check failed', err);
        return NO_ENTITLEMENT;
      }
    },
  };
}

/**
 * Premium from either source: the store on this device first (it knows
 * renewals first), else the account (bought on another device).
 */
export function eitherEntitlement(store: EntitlementSource, account: EntitlementSource): EntitlementSource {
  let lastUserId: string | null = null;
  const check = async (userId: string) => {
    lastUserId = userId;
    const [s, a] = await Promise.all([store.check(userId), account.check(userId)]);
    return s.active ? s : a;
  };
  return {
    check,
    subscribe: store.subscribe
      ? (onChange) =>
          store.subscribe!((s) => {
            if (s.active || !lastUserId) return onChange(s);
            void account.check(lastUserId).then(onChange);
          })
      : undefined,
  };
}
